import { API_URL } from "@/shared/config";
import { AUTH_ENDPOINTS } from "@/constants/api.auth";

export const TOKEN_KEY = "token";
export const REFRESH_KEY = "refresh";

let refreshPromise: Promise<string | null> | null = null;

type TokenRefreshListener = (accessToken: string) => void;
const listeners = new Set<TokenRefreshListener>();

export const registerTokenRefreshListener = (
  listener: TokenRefreshListener
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const notifyTokenRefreshed = (accessToken: string) => {
  listeners.forEach((listener) => {
    try {
      listener(accessToken);
    } catch (error) {
      console.error("Token refresh listener failed:", error);
    }
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("auth:tokens-updated", { detail: { token: accessToken } })
    );
  }
};

export const getStoredAccessToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
};

export const setStoredTokens = (access: string, refresh: string) => {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
  notifyTokenRefreshed(access);
};

export const clearStoredTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

export const isAuthApiUrl = (url: string): boolean => {
  return (
    url.includes("/users/token-auth/") || url.includes("/users/token-refresh/")
  );
};

export async function refreshAccessToken(
  bareFetch: typeof fetch
): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refresh = getStoredRefreshToken();
    if (!refresh) {
      return null;
    }

    try {
      const response = await bareFetch(`${API_URL}${AUTH_ENDPOINTS.TOKEN_REFRESH}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (!data?.token || !data?.refresh) {
        return null;
      }

      setStoredTokens(data.token, data.refresh);
      return data.token as string;
    } catch (error) {
      console.error("Failed to refresh access token:", error);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export const withAuthorizationHeader = (
  init: RequestInit | undefined,
  accessToken: string
): RequestInit => {
  const headers = new Headers(init?.headers ?? undefined);
  headers.set("Authorization", `token ${accessToken}`);
  return { ...init, headers };
};
